import * as React from "react";
import { Box, Snackbar } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { Link, Typography } from "ots-components-library";
import CloseIconSvg from "../../assets/close-icon.svg?react";
import { hoverEffect } from "../../utils/stylingConsts";

import { LocationsListViewItem } from "./LocationsListViewItem";

import { useAppDispatch, useAppSelector } from "../../hooks";

import type { Location } from "../../types";

import "./LocationsListView.scss";
import {
  selectFavoritesList,
  addFavorite,
  selectShowFavoritedLocations,
} from "../../redux/features/locationsSlice";

export interface LocationsListViewProps {
  locations: Location[];
  onScrollHandler: (event: React.UIEvent<HTMLElement>) => any;
}

function LocationsListView({
  locations,
  onScrollHandler,
}: LocationsListViewProps) {
  const dispatch = useAppDispatch();
  const [toastOpen, setToastOpen] = React.useState(false);
  const [unbookmarkLocationId, setUnbookmarkLocationId] = React.useState("");
  const showFavoritedLocations = useAppSelector(selectShowFavoritedLocations);

  const handleAutomaticClose = (
    event: React.SyntheticEvent<any> | Event,
    reason: string,
  ) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };
  const handleClose = () => {
    setToastOpen(false);
  };

  const handleBookmarkClick = async (
    locationId: string,
    isUnbookmarkClick: boolean,
  ) => {
    if (isUnbookmarkClick) {
      setToastOpen(false);
      await new Promise((r) => setTimeout(r, 5));
      setUnbookmarkLocationId(locationId);
      setToastOpen(true && showFavoritedLocations);
    } else {
      setToastOpen(false);
    }
  };

  const undo = () => {
    dispatch(addFavorite(unbookmarkLocationId));
    handleClose();
  };

  const message = (
    <Typography variant="body2" fontWeight="medium">
      Bookmark removed.
    </Typography>
  );

  const action = (
    <>
      <Button
        sx={hoverEffect}
        className="undo-button"
        color="secondary"
        size="small"
        onClick={undo}
      >
        <Typography variant="body2" fontWeight="medium" onClick={undo}>
          UNDO
        </Typography>
      </Button>
      <IconButton
        sx={hoverEffect}
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIconSvg transform="scale(0.8, 0.8)" />
      </IconButton>
    </>
  );

  const error = useAppSelector((state) => state.locations.error);
  const favoritesList = useAppSelector(selectFavoritesList);

  if (error)
    return (
      <Typography color="primary">
        There was an error fetching locations.
        <br />
        Please try refreshing the page, or send an email to the{" "}
        <Link
          variant="inline"
          href="mailto:!Franchise-Availability-Report@chick-fil-a.com"
        >
          Franchise Availability Report team
        </Link>
        .
      </Typography>
    );
  return (
    <Box minHeight={0}>
      <>
        {locations.length === 0 ? (
          <div className="list-view-placeholder">
            <Typography>No currently pending opportunities.</Typography>
          </div>
        ) : (
          <Box className="locations-list" onScroll={onScrollHandler}>
            {locations.map((l) => (
              <LocationsListViewItem
                key={l.locationNumber}
                location={l}
                // this "includes" could be ran outside for better performance,
                // but since favoritesList is so small i didn't think it would be worth it
                isFavorited={favoritesList.includes(l.locationNumber)}
                handleBookmarkClick={handleBookmarkClick}
              />
            ))}
          </Box>
        )}
      </>
      <Snackbar
        open={toastOpen && showFavoritedLocations}
        autoHideDuration={6000}
        message={message}
        action={action}
        onClose={handleAutomaticClose}
      />
    </Box>
  );
}

export default LocationsListView;
